<template>
	<div class="db2-energy" :class="'is-' + variant">
		<template v-if="variant === 'opportunities'">
			<div class="db2-energy-head">
				<div class="db2-energy-title">
					<v-icon v-if="isEditMode" small class="db2-card-drag" @mousedown.native.stop="onDragStart">mdi-drag</v-icon>
					<span>{{ card.name }}</span>
				</div>
			</div>
			<div class="db2-energy-ops">
				<div class="op" v-for="(op, i) in ops" :key="i">
					<div>
						<div class="name">{{ op.name }}</div>
						<div class="save">Est. {{ op.save }}</div>
					</div>
					<button type="button" class="haq-btn" disabled>Review</button>
				</div>
			</div>
		</template>
		<template v-else>
			<div class="db2-energy-side">
				<div class="db2-energy-title">
					<v-icon v-if="isEditMode" small class="db2-card-drag" @mousedown.native.stop="onDragStart">mdi-drag</v-icon>
					<span>{{ card.name }}</span>
				</div>
				<div class="db2-energy-hero">
					<div class="val">{{ total }} <span>kWh</span></div>
					<div class="label">Total Usage</div>
					<div class="delta down">
						<v-icon>mdi-triangle-down</v-icon>
						8.4% vs last week
					</div>
				</div>
			</div>
			<HaqEChart class="db2-energy-chart" :option="option" height="100%" />
		</template>
	</div>
</template>

<script>
import HaqEChart from '../HaqEChart.vue';
import { mockPointData, mockAxisLabels } from '../mockPointData';
import { echarts, chartTheme, baseChartOption } from '../chartTheme';

/** Axis tick / name size. Set on each axis so baseChartOption 15 / inherit cannot win. */
const AXIS_FONT = 11;

/**
 * Mix a hex/rgb color toward black by `amount` (0.4 = 40% darker, not transparent).
 * @param {string} color
 * @param {number} amount
 * @returns {string}
 */
function darkenColor(color, amount) {
	const factor = 1 - amount;
	const hex = String(color || '').replace('#', '');
	if (/^[0-9a-fA-F]{6}$/.test(hex)) {
		const n = parseInt(hex, 16);
		const r = Math.round(((n >> 16) & 255) * factor);
		const g = Math.round(((n >> 8) & 255) * factor);
		const b = Math.round((n & 255) * factor);
		return `rgb(${r}, ${g}, ${b})`;
	}
	const m = String(color || '').match(/rgba?\(\s*(\d+)\s*,\s*(\d+)\s*,\s*(\d+)/i);
	if (m) {
		return `rgb(${Math.round(+m[1] * factor)}, ${Math.round(+m[2] * factor)}, ${Math.round(+m[3] * factor)})`;
	}
	return color || '#5b8def';
}

/**
 * Energy total plus bars or opportunity list.
 */
export default {
	name: 'EnergyCard',
	components: { HaqEChart },
	props: {
		card: { type: Object, required: true },
		isEditMode: { type: Boolean, default: false },
		timePeriod: { type: String, default: 'week' },
		refreshKey: { type: Number, default: 0 }
	},
	computed: {
		/**
		 * Visual variant.
		 * @returns {string}
		 */
		variant() {
			const v = (this.card.config && this.card.config.variant) || 'bars';
			if (v === 'opportunities') return 'bars';
			return v;
		},
		/**
		 * Dashboard header time range.
		 * @returns {string}
		 */
		range() {
			return this.timePeriod || 'week';
		},
		/**
		 * Mock series for the selected range.
		 * @returns {object}
		 */
		data() {
			return mockPointData(this.card.uniqueId, this.range, { base: 180, unit: 'kWh', refreshKey: this.refreshKey });
		},
		/**
		 * Bars shown in the compact chart.
		 * @returns {number[]}
		 */
		bars() {
			const series = this.data.series || [];
			if (this.range === 'week') return series.slice(-7);
			return series;
		},
		/**
		 * Display total.
		 * @returns {string}
		 */
		total() {
			const cfg = this.card.config || {};
			if (cfg.total != null) return Number(cfg.total).toLocaleString();
			return Math.round(this.bars.reduce((s, v) => s + v, 0)).toLocaleString();
		},
		/**
		 * Opportunity rows.
		 * @returns {Array}
		 */
		ops() {
			return [
				{ name: 'Schedule adjustments', save: '$4,200 / yr' },
				{ name: 'Unoccupied setback', save: '$2,150 / yr' },
				{ name: 'Chiller staging', save: '$1,680 / yr' }
			];
		},
		/**
		 * Bar option with visible axes.
		 * @returns {object}
		 */
		option() {
			const t = chartTheme();
			const color = t.accent || '#5b8def';
			return {
				...baseChartOption(t),
				// Drop inherit so canvas can honor AXIS_FONT (11).
				textStyle: { fontSize: AXIS_FONT },
				grid: { left: 8, right: 8, top: 6, bottom: 4, containLabel: true },
				xAxis: {
					type: 'category',
					data: mockAxisLabels(this.range, this.bars.length),
					textStyle: { fontSize: AXIS_FONT },
					nameTextStyle: { fontSize: AXIS_FONT },
					axisLabel: {
						color: t.muted,
						fontSize: AXIS_FONT,
						interval: 0,
						hideOverlap: false,
						rotate: 0,
						margin: 8,
						letterSpacing: -0.3
					},
					axisLine: { show: true, lineStyle: { color: t.line } },
					axisTick: { show: false }
				},
				yAxis: {
					type: 'value',
					textStyle: { fontSize: AXIS_FONT },
					nameTextStyle: { fontSize: AXIS_FONT },
					splitLine: { show: true, lineStyle: { color: t.line } },
					axisLine: { show: false },
					axisTick: { show: false },
					axisLabel: {
						show: true,
						color: t.muted,
						fontSize: AXIS_FONT,
						formatter: '{value} kWh'
					}
				},
				series: [{
					type: 'bar',
					data: this.bars,
					barWidth: '65%',
					barMaxWidth: 36,
					itemStyle: {
						borderRadius: [3, 3, 0, 0],
						color: new echarts.graphic.LinearGradient(0, 0, 0, 1, [
							{ offset: 0, color },
							{ offset: 1, color: darkenColor(color, 0.4) }
						])
					}
				}]
			};
		}
	},
	methods: {
		/**
		 * Start a canvas move from the in-card title handle.
		 * @param {MouseEvent} e
		 */
		onDragStart(e) {
			if (!this.isEditMode) return;
			this.$emit('drag-start', e);
		}
	}
};
</script>
